import { Collection } from 'firestorter';
import format from 'string-template';
import moment from 'moment';

import { firestorePaths, pathPlaceholder } from '../../utils/paths';
import BaseDocument from '../../Model/BaseDocument';

const PROMOTION_STATUS = {
  ONGOING: 'ONGOING',
  UPCOMING: 'UPCOMING',
};

/**
 * class representing promotions by a coach
 *
 * @class Promotion
 * @extends BaseDocument
 */
class Promotion extends BaseDocument {
  /**
   * Get promotion description
   * @return {string}
   */
  get description() {
    return this.data?.description || '';
  }

  /**
   * Get promotion start date in local time
   * @return {Date|void}
   */
  get startDate() {
    const utcDate = this.data.startDate?.toDate();
    return utcDate ? moment.utc(utcDate).local().toDate() : undefined;
  }

  /**
   * Get promotion end date in local time
   * @return {Date|void}
   */
  get endDate() {
    const utcDate = this.data.endDate?.toDate();
    return utcDate ? moment.utc(utcDate).local().toDate() : undefined;
  }

  /**
   * Get coach ID
   * @return {string}
   */
  get coachId() {
    return this.data.coachId;
  }

  /**
   * Get promotion deleted status
   * @return {boolean}
   */
  get isDeleted() {
    return !!this.data.isDeleted;
  }

  /**
   * Check if promotion is currently active
   * @return {boolean}
   */
  isOngoing() {
    const now = new Date();
    const { startDate } = this;
    const { endDate } = this;

    return startDate && endDate && now >= startDate && now <= endDate;
  }

  /**
   * Update promotion data
   * @param {object} data - Data to update
   * @return {Promise<void>}
   */
  async updateDoc(data) {
    await this.updateFields(data);
  }

  /**
   * Delete promotion
   * @return {Promise<void>}
   */
  async delete() {
    return this.updateFields({
      isDeleted: true,
      deletedAt: new Date(),
    });
  }

  /**
   * Create a new promotion
   * @param {object} promotionData - Promotion data
   * @return {Promise<Promotion>}
   */
  static async createPromotion(promotionData) {
    const promotionCollection = new Collection(format(firestorePaths.COACH_PROMOTIONS, {
      [pathPlaceholder.COACH_ID]: promotionData.coachId,
    }));

    promotionCollection.add(promotionData);
  }

  /**
   * Get all promotions for a coach
   * @param {string} coachId - The coach ID
   * @return {Promise<Array>}
   */
  static async getCurrentPromotionsByCoachId(coachId) {
    const promotionsCollection = new Collection(
      format(firestorePaths.COACH_PROMOTIONS, { [pathPlaceholder.COACH_ID]: coachId }),
      {
        createDocument: (source, options) => new Promotion(source, options),
        query: (query) => query.where('endDate', '>=', moment.utc().toDate()),
      },
    );
    await Promotion.initCollection(promotionsCollection);
    return promotionsCollection;
  }
}

export default Promotion;
export {
  PROMOTION_STATUS,
};
